/**
 * Media Uploader Component Types
 *
 * Type definitions for the YAML-config-driven media uploader component.
 * Supports uploads to AWS S3 (pre-signed URLs) and Azure Blob Storage (SAS tokens).
 */

// ============================================================================
// File Status State Machine
// ============================================================================

/**
 * File upload status enum representing the state machine states.
 *
 * Flow: pending -> hashing -> initiating -> uploading -> completing -> verifying -> success
 *                                                                            \-> error (at any step)
 */
export type FileStatus =
  | 'pending'
  | 'hashing'
  | 'initiating'
  | 'uploading'
  | 'completing'
  | 'verifying'
  | 'success'
  | 'error'

// ============================================================================
// Storage Provider Types
// ============================================================================

/**
 * Supported storage providers.
 * The backend returns this value in the initiate response - client MUST use it
 * to select the correct upload implementation. Never infer from URL shape.
 */
export type StorageProvider = 'aws_s3' | 'azure_blob'

// ============================================================================
// File Item Interface
// ============================================================================

/**
 * Represents a file in the upload queue with its current state.
 */
export interface FileItem {
  /** Client-generated UUID for tracking */
  id: string
  /** The File object from the browser file API */
  file: File
  /** Current status in the upload state machine */
  status: FileStatus
  /** Upload progress percentage (0-100) */
  progress: number
  /** SHA-256 checksum in lowercase hex format (calculated client-side) */
  checksum?: string
  /** Human-readable error message */
  error?: string
  /** Machine-readable error code for retry logic */
  errorCode?: string
  /** Upload result on success */
  result?: UploadResult
  /** Number of retry attempts for this file */
  retryCount: number
}

// ============================================================================
// Upload Result Types
// ============================================================================

/**
 * Result returned after successful upload verification.
 */
export interface UploadResult {
  /** Server-assigned upload ID */
  uploadId: string
  /** Server-assigned file record ID */
  fileRecordId: string
  /** Original file name */
  fileName: string
  /** Storage path (S3 key or Azure Blob path) */
  objectKey: string
  /** File MIME type */
  contentType: string
  /** File size in bytes */
  fileSize: number
  /** SHA-256 checksum in lowercase hex */
  checksum: string
}

// ============================================================================
// API Response Types
// ============================================================================

/**
 * Response from the /upload/initiate endpoint.
 */
export interface InitiateUploadResponse {
  /** Server-assigned upload ID for tracking */
  uploadId: string
  /** Storage provider to use - drives which upload implementation is invoked */
  storageProvider: StorageProvider
  /** Pre-signed URL (S3) or SAS token URL (Azure Blob) */
  uploadUrl: string
  /** Provider-specific headers that MUST be set on the storage PUT request */
  requiredHeaders: Record<string, string>
}

/**
 * Response from the /upload/complete endpoint.
 */
export interface CompleteUploadResponse {
  /** Server verification status */
  status: 'completed' | 'failed'
  /** File record on success */
  fileRecord?: UploadResult
  /** Error details on failure */
  error?: {
    /** Machine-readable error code */
    code: string
    /** Human-readable error message */
    message: string
  }
}

// ============================================================================
// State Management Types
// ============================================================================

/**
 * Uploader state managed by useReducer.
 * IMPORTANT: activeUploadsCount is a NUMBER, not a boolean.
 */
export interface UploaderState {
  /** Array of files in the upload queue */
  files: FileItem[]
  /** Number of concurrent uploads in progress (NOT a boolean) */
  activeUploadsCount: number
  /** Number of successfully completed uploads */
  completedCount: number
  /** Number of failed uploads */
  failedCount: number
}

/**
 * Discriminated union of all uploader actions.
 */
export type UploaderAction =
  | { type: 'ADD_FILES'; files: File[] }
  | { type: 'REMOVE_FILE'; id: string }
  | { type: 'UPDATE_FILE'; id: string; updates: Partial<FileItem> }
  | { type: 'START_UPLOAD'; id: string }
  | { type: 'FINISH_UPLOAD'; id: string }
  | { type: 'UPLOAD_SUCCESS'; id: string; result: UploadResult }
  | { type: 'UPLOAD_ERROR'; id: string; error: string; errorCode?: string }
  | { type: 'RETRY_FILE'; id: string }
  | { type: 'CLEAR_COMPLETED' }
  | { type: 'RESET' }

// ============================================================================
// Configuration Types
// ============================================================================

/**
 * Resource context for the upload - defines where files belong.
 */
export interface ResourceContext {
  /** Entity type (e.g., 'device', 'organization') */
  entityType: string
  /** Form resource identifier */
  formResource: string
  /** Destination path within storage */
  destination: string
}

/**
 * UI labels for internationalization.
 */
export interface UploaderLabels {
  /** Dropzone instructions text */
  dropzone: string
  /** File selection button text */
  button: string
  /** Uploading status text */
  uploading: string
  /** Checksum calculation status text */
  calculating: string
  /** Success status text */
  success: string
  /** Remove file button text */
  remove: string
  /** Retry button text */
  retry: string
}

/**
 * Complete uploader configuration from YAML.
 */
export interface UploaderConfig {
  /** Allowed MIME types (e.g., ['image/jpeg', 'image/png']) */
  allowedTypes: string[]
  /** Maximum file size in bytes */
  maxFileSize: number
  /** Maximum number of files per upload session */
  maxFiles?: number
  /** Allow multiple file selection */
  multiFile?: boolean
  /** Show drag-and-drop zone */
  dragAndDrop?: boolean
  /** Upload immediately on file selection */
  autoUpload?: boolean
  /** Show image thumbnails for previews */
  showPreview?: boolean
  /** Display file sizes */
  showFileSize?: boolean
  /** Maximum concurrent uploads (default: 3) */
  maxConcurrentUploads?: number
  /** Calculate SHA-256 checksum before upload (default: true) */
  calculateChecksum?: boolean
  /** Show/hide the entire file list component (default: true) */
  showFileList?: boolean
  /** Show/hide the overall progress bar (default: true) */
  showProgressBar?: boolean
  /** Show/hide the summary text "X of Y files uploaded" (default: true) */
  showSummaryText?: boolean
  /** Explicit control over manual upload button visibility (default: based on autoUpload) */
  showUploadButton?: boolean
  /** Resource context for upload destination */
  resourceContext: ResourceContext
  /** UI labels for i18n */
  labels?: UploaderLabels
}

// ============================================================================
// Component Props Types
// ============================================================================

/**
 * Overall upload progress summary.
 */
export interface OverallProgress {
  /** Total number of files in queue */
  totalFiles: number
  /** Number of successfully completed files */
  completedFiles: number
  /** Number of failed files */
  failedFiles: number
  /** Number of concurrent uploads in progress (NOT a boolean) */
  activeUploads: number
  /** Weighted average progress percentage (0-100) */
  overallPercent: number
}

/**
 * Props for the MediaUploader component.
 */
export interface MediaUploaderProps {
  /** Configuration: either YAML string (imported with ?raw) or UploaderConfig object */
  config: string | UploaderConfig
  /** ID of the entity files attach to */
  entityId: string
  /** Callback when all uploads complete successfully */
  onUploadComplete?: (results: UploadResult[]) => void
  /** Callback when a file upload fails */
  onUploadError?: (error: UploadError, file: FileItem) => void
  /** Callback when files array changes */
  onFilesChange?: (files: FileItem[]) => void
  /** Callback when overall progress changes */
  onProgressChange?: (progress: OverallProgress) => void
  /** Disable the entire uploader */
  disabled?: boolean
  /** External trigger for starting uploads when autoUpload is false */
  triggerUpload?: boolean
  /** Additional CSS class names */
  className?: string
  /**
   * Enable mock mode for development/testing.
   * When true, skips actual API calls and storage uploads,
   * simulating success with mock data after a short delay.
   */
  mockMode?: boolean
  /** Delay in ms for mock uploads (default: 1500) */
  mockDelay?: number
}

// ============================================================================
// Error Types
// ============================================================================

/**
 * Custom error class for upload errors with machine-readable codes.
 */
export class UploadError extends Error {
  /** Machine-readable error code */
  code: string

  constructor(code: string, message: string) {
    super(message)
    this.name = 'UploadError'
    this.code = code
    // Maintain proper stack trace for where error was thrown (V8 engines)
    if (typeof (Error as any).captureStackTrace === 'function') {
      ;(Error as any).captureStackTrace(this, UploadError)
    }
  }
}
